import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Col, FormControl, InputGroup, ListGroup, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ActionButton from 'components/common/ActionButton';
import Flex from 'components/common/Flex';

/**
 * Componente para un ítem individual del carrito
 */
const ProductoCarritoItem = ({
    product,
    index,
    onEliminar,
    onActualizar,
    onToggleEditar
}) => {
    return (
        <tr key={index} className="border-bottom">
            <th className="ps-0" colSpan="2">
                <Row>
                    <Col className="col-xxl-7 col-xl-12 col-lg-8 col-md-12 col-sm-12 col-12">
                        <th className={classNames('ps-0', { 'pt-0': index === 0 })}>
                            <Row>
                                {/* Botón Eliminar */}
                                <Col xl={1} lg={1} md={1} sm={1} xs={1} xxs={1}>
                                    <Link to="#" onClick={onEliminar}>
                                        <img src="/img/cancelar30.png" alt="Eliminar" />
                                    </Link>
                                </Col>

                                {/* Imagen del Producto */}
                                <Col xl={2} lg={2} md={2} sm={2} xs={4} xxs={3}>
                                    {product.vCodigoBarras === 'LIC-SERV' && (
                                        <img src="/img/servidor48.png" width="45" alt="Servidor" />
                                    )}
                                    {product.vCodigoBarras === 'LIC-CAJ' && (
                                        <img src="/img/caja48.png" width="45" alt="Caja" />
                                    )}
                                    {product.vCodigoBarras === 'LIC-NUB' && (
                                        <img src="/img/nube48.png" width="45" alt="Nube" />
                                    )}
                                </Col>

                                {/* Detalles del Producto */}
                                <Col>
                                    {product.vNombreProducto}
                                    <div className="text-400 fw-normal fs--2">
                                        {product.dCantidad} X {product.vCodigoBarras} &nbsp;&nbsp;&nbsp;&nbsp;
                                        UNIDAD: {product.vClaveUnidad}&nbsp;&nbsp;&nbsp;
                                        IVA: {product.dIVA.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                        <br />
                                        Precio Unitario (IVA Incluido) {(product.dPrecioUnitario * 1.16).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </div>

                                    {/* Badge de Renovación */}
                                    {product.vTipoRenovacion && (
                                        <div className="text-400 fw-normal fs--2 mt-1">
                                            <span className="badge bg-info">
                                                Renovación: {product.vTipoRenovacion.charAt(0).toUpperCase() + product.vTipoRenovacion.slice(1)}
                                            </span>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </th>
                    </Col>

                    {/* Precio y Controles de Edición */}
                    <Col className="p-0 col-xxl-5 col-xl-12 col-lg-4 col-md-12 col-sm-12 col-12 d-flex flex-row-reverse">
                        <th className="align-middle">
                            <Flex direction='row-reverse'>
                                {!product.bEdit ? (
                                    <>
                                        <ListGroup horizontal className={(product.dTotal === 0) && "border-danger border"}>
                                            <ListGroup.Item onDoubleClick={() => onToggleEditar(product.index, true)}>
                                                {product.dTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                            </ListGroup.Item>
                                            <ListGroup.Item>
                                                <ActionButton 
                                                    onClick={() => onToggleEditar(product.index, true)} 
                                                    icon="edit" 
                                                    title="Editar" 
                                                    variant="action" 
                                                    className="p-0 me-2" 
                                                />
                                            </ListGroup.Item>
                                        </ListGroup>
                                    </>
                                ) : (
                                    <InputGroup className="fs--1">
                                        <FormControl 
                                            type="number" 
                                            size="sm" 
                                            className="text-center" 
                                            id={`dCantidad${product.index}`} 
                                            defaultValue={product.dCantidad} 
                                        />
                                        &nbsp;&nbsp;
                                        <div className="align-self-center fw-bold">
                                            <span> X </span>
                                        </div>
                                        &nbsp;&nbsp;
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <FormControl 
                                            style={{ width: '80px' }} 
                                            type="number" 
                                            size="sm" 
                                            className="text-center" 
                                            id={`dPrecioUnitarioIVA${product.index}`} 
                                            defaultValue={(product.dTotal / product.dCantidad).toFixed(2)} 
                                        />
                                        <Button 
                                            onClick={() => {
                                                const cantidad = document.getElementById(`dCantidad${product.index}`).value;
                                                const precioIVA = document.getElementById(`dPrecioUnitarioIVA${product.index}`).value;
                                                onActualizar(product.index, cantidad, precioIVA);
                                            }} 
                                            variant="outline-secondary" 
                                            id="button-addon2"
                                        >
                                            <img src="/img/check25.png" alt="Confirmar" />
                                        </Button>
                                    </InputGroup>
                                )}
                            </Flex>
                        </th>
                    </Col>
                </Row>
            </th>
        </tr>
    );
};

ProductoCarritoItem.propTypes = {
    product: PropTypes.shape({
        index: PropTypes.number.isRequired,
        idProducto: PropTypes.number.isRequired,
        vCodigoBarras: PropTypes.string.isRequired,
        vNombreProducto: PropTypes.string.isRequired,
        dPrecioUnitario: PropTypes.number.isRequired,
        dCantidad: PropTypes.number.isRequired,
        dImporte: PropTypes.number.isRequired,
        dDescuento: PropTypes.number,
        dIVA: PropTypes.number.isRequired,
        dTotal: PropTypes.number.isRequired,
        vClaveUnidad: PropTypes.string.isRequired,
        bEdit: PropTypes.bool,
        vTipoProducto: PropTypes.string,
        iTipoPago: PropTypes.number,
        vTipoRenovacion: PropTypes.string,
        idProductoRenovaciones: PropTypes.number,
        dTasaCuotaIVA: PropTypes.number,
        vTipoFactorIVA: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired,
    onEliminar: PropTypes.func.isRequired,
    onActualizar: PropTypes.func.isRequired,
    onToggleEditar: PropTypes.func.isRequired
};

export default ProductoCarritoItem;
